import React, { useEffect, useState } from 'react'
import Header from '../../components/Header/Header'
import BreadCrumbs from '../../components/BreadCrumbs/BreadCrumbs'
import { Box, Container } from '@mui/material'
import ScrollToTop from '../../components/ScrollToTop/ScrollToTop'
import Footer from '../../components/Footer/Footer'
import ChatPopup from '../../components/ChatPopup/ChatPopup'
import { useOptions } from '../../context/optionContext'
import { canAccessChatbot } from '../../utilities/pageAccess'
import { Route, Routes, useLocation } from 'react-router-dom'
import { ROUTES } from '../../constants'
import Home from '../../pages/Home/Home'
import MyMachines from '../../pages/MyMachines/MyMachines'
import Products from '../../pages/Products/Products'
import SingleProduct from '../../pages/SingleProduct/SingleProduct'
import Recommendations from '../../pages/Recommendations/Recommendations'
import Cart from '../../pages/Cart/Cart'
import Checkout from '../../pages/Checkout/Checkout'
import OrderConfirmation from '../../pages/OrderConfirmation/OrderConfirmation'
import OrderHistory from '../../pages/OrderHistory/OrderHistory'
import Settings from '../../pages/Settings/Settings'
import Profile from '../../pages/Profile/Profile'
import Admin from '../../pages/Admin/Admin'
import Rspl from '../../pages/Rspl/Rspl'
import Documents from '../../pages/Documents/Documents'
import Requests from '../../pages/Requests/Requests'
import Support from '../../pages/Support/Support'
import Analytics from '../../pages/Analytics/Analytics'
import ProtectedRoute from '../../ProtectedRoute'
import AdminRoute from '../../AdminRoute'
import PDFProductLinkMachineFinder from '../../components/PDFProductLinkMachineFinder/PDFProductLinkMachineFinder'
import Services from '../../pages/Services/Services'
import SavedForLater from "../../pages/SavedForLater/SavedForLater";
import PageNotFound from '../../pages/PageNotFound/PageNotFound'
import AdminV2 from '../../pages/Admin/AdminV2'
import SearchResults from '../../pages/SearchResults/SearchResults'
import TrackOrder from '../../pages/TrackOrder/TrackOrder'
import OrderFailure from '../../pages/OrderFaliure/OrderFaliure'
import NotificationsPage from '../../pages/Notifications/Notifications'
import ModelViewer from '../../pages/ModelViewer/ModelViewer'
import MarkdownHelpPage from '../../components/MarkDown/MarkeDownPopup'
import NotAuthorized from '../../components/NotAuthorized/NotAuthorized'
import { useAuth } from '../../context/authContext'
import { canAccessCart, canAccessOrderAnalytics, canAccessSettings, canAccessUserAnalytics } from '../../utilities/pageAccess'
import UserAnalytics from '../../pages/UserAnalytics/UserAnalytics'
import BannerNotice from '../../components/BannerNotice/BannerNotice'

const DefaultLayout = () => {
    const location = useLocation();
    const { user, hasPermission } = useAuth();
    const { optionKeys } = useOptions();
    const [chatOpen, setChatOpen] = useState(false);

    // DefaultLayout only ever renders on already-protected (non-public) routes, so no
    // separate public-route check is needed here (App.js's old floating-popup mount used
    // to duplicate that check for itself before this moved in-layout).
    const showChatbot = optionKeys?.enableChatbot !== "false" && canAccessChatbot(hasPermission, user);

    // Measures the actual rendered header height so ChatPopup.css's fixed panel can
    // dock its top edge flush below the real navbar. Queries the AppBar's own DOM
    // node directly by its stable className (rather than wrapping <Header/> in a
    // ref'd Box) — a wrapper div around a position:"sticky" AppBar was found to
    // break the header's own stickiness. Mirrors Layout2.js exactly.
    const [headerHeight, setHeaderHeight] = useState(64);
    useEffect(() => {
        const node = document.querySelector(".header-main");
        if (!node) return undefined;
        const observer = new ResizeObserver((entries) => {
            const height = entries?.[0]?.contentRect?.height;
            if (height) setHeaderHeight(height);
        });
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    const noBreadcrumPaths = [ROUTES.HOME, ROUTES.HOME_ALIAS, "/admin-v2", "/admin", "/model-viewer"];
    return (
        <Box
            display="flex"
            flexDirection="column"
            minHeight="100vh"
            width={"100vw"}
            sx={{ "--chip-header-h": `${headerHeight}px` }}
        >
            <Header chatOpen={chatOpen} setChatOpen={setChatOpen} showChatbot={showChatbot} />
            {/* CHIP is a position:fixed panel (ChatPopup.css) — independent of page scroll/height.
                The content column reserves the panel's width via marginRight:var(--chip-panel-w)
                (set by ChatPopup.js, 0 when closed) so the page reflows into the remaining space
                instead of being covered/chopped by the panel. Mirrors Layout2.js's content column
                exactly (see that file). overflowX:auto is a safety net for any page whose own
                markup uses a fixed/viewport-relative width (e.g. Checkout's width:"100vw") that
                doesn't shrink with marginRight — such content scrolls into view within the
                available width instead of spilling under the fixed panel. */}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    minWidth: 0,
                    marginRight: "var(--chip-panel-w, 0px)",
                    transition: "margin-right 0.28s ease",
                    overflowX: "auto",
                }}
            >
                <BannerNotice />

                {!noBreadcrumPaths.includes(location.pathname) && (
                    <BreadCrumbs />
                )}
                <Container maxWidth={false} component="main" className="main-container">
                        <Routes>
                            <Route path={ROUTES.HOME} element={<Home />} />
                            <Route path={ROUTES.HOME_ALIAS} element={<Home />} />
                            <Route path={ROUTES.MACHINES} element={<MyMachines />} />
                            <Route path={`/${ROUTES.MACHINES}/:machineID`} element={<Products />} />
                            <Route path={`/${ROUTES.MACHINES}/:machineID/:productID`} element={<SingleProduct />} />
                            <Route path={ROUTES.RECOMMENDATIONS} element={<Recommendations />} />
                            <Route path={ROUTES.CART} element={<Cart />} />
                            <Route path={ROUTES.CHECKOUT} element={<Checkout />} />
                            <Route path={ROUTES.ORDER_RECEIVED} element={<OrderConfirmation />} />
                            <Route path={"/order-failure"} element={<OrderFailure />} />
                            <Route path={ROUTES.ORDER_ERROR} element={<OrderConfirmation />} />
                            <Route path={ROUTES.ORDER_HISTORY} element={<OrderHistory />} />
                            <Route path={ROUTES.ORDER_PRODUCT_DETAILS} element={<OrderHistory />} />
                            <Route path={ROUTES.ORDERS_DETAILS} element={<OrderHistory />} />
                            <Route path={ROUTES.TRACK_ORDER} element={<TrackOrder />} />
                            <Route path={ROUTES.SETTINGS} element={canAccessSettings(user) ? <Settings /> : <NotAuthorized />} />
                            <Route path={ROUTES.PROFILE} element={<Profile />} />
                            <Route path={ROUTES.ADMIN} element={<Admin />} />
                            <Route path={ROUTES.RSPL} element={<Rspl />} />
                            <Route path={ROUTES.DOCUMENT} element={<Documents />} />
                            <Route path={ROUTES.DOCUMENT_DETAILS} element={<Documents />} />
                            <Route path={ROUTES.REQUESTS} element={<Requests />} />
                            <Route path={ROUTES.SUPPORT} element={<Support />} />
                            <Route path={ROUTES.SAVED_FOR_LATER} element={<SavedForLater />} />
                            <Route path={ROUTES.ANALYTICS} element={canAccessOrderAnalytics(hasPermission, user) ? <Analytics /> : <NotAuthorized />} />
                            <Route path={ROUTES.USER_ANALYTICS} element={canAccessUserAnalytics(hasPermission, user) ? <UserAnalytics /> : <NotAuthorized />} />
                            <Route path={ROUTES.SERVICES} element={<Services />} />
                            <Route path={ROUTES.SEARCH_RESULTS} element={<SearchResults />} />
                            <Route path={ROUTES.NOTIFICATIONS} element={<NotificationsPage />} />
                            <Route path={`${ROUTES.FINDER}/:product_id`} element={<PDFProductLinkMachineFinder />} />
                            <Route path={ROUTES.MARKDOWN} element={<MarkdownHelpPage />} />

                            <Route path={ROUTES.PAGE_NOT_FOUND} element={<PageNotFound />} />
                        </Routes>
                    </Container>
                    <Footer />
                </Box>
            {showChatbot && <ChatPopup open={chatOpen} onClose={() => setChatOpen(false)} />}
            <ScrollToTop />
        </Box>
    )
}

export default DefaultLayout
